import type { CSSProperties } from 'react';
import { figmaColorScales, figmaSemanticColors } from '../../../core/tokens';
import { kaiTypographyWeb, pxToRem } from '../../typography';
import type { AdminChromePageTitleBarProps } from './types';

const HEADER_COLORS = {
  background: figmaColorScales.neutral[900],
  border: figmaColorScales.neutral[700],
  text: figmaSemanticColors.text.inverse,
} as const;

const HEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 60,
  padding: '0 15px',
  background: HEADER_COLORS.background,
  color: HEADER_COLORS.text,
  borderBottom: `1px solid ${HEADER_COLORS.border}`,
  boxSizing: 'border-box',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  gap: 0,
};

const LEFT_SECTION_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  gap: 20,
};

const TITLE_GROUP_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
  color: HEADER_COLORS.text,
  overflow: 'hidden',
  padding: 0,
  border: 0,
  background: 'transparent',
};

const TITLE_TEXT_STYLE: CSSProperties = {
  ...kaiTypographyWeb.largeTitleBold,
  lineHeight: 1.4, // matches largeTitleBold's font-size (28px / 20px)
  letterSpacing: '0px',
  color: HEADER_COLORS.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const TITLE_DIVIDER_STYLE: CSSProperties = {
  width: 1,
  height: pxToRem(24),
  minHeight: '24px',
  background: HEADER_COLORS.border,
  flex: '0 0 auto',
};

const ACTIONS_STYLE: CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};

const ORG_WIDGET_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...kaiTypographyWeb.xSmallBold,
  lineHeight: 1.333, // matches xSmallBold's font-size (16px / 12px)
  color: HEADER_COLORS.text,
  letterSpacing: '0.24px',
  paddingRight: 18,
  whiteSpace: 'nowrap',
};

const STATUS_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: figmaColorScales.green[400],
};

export const AdminChromePageTitleBar = ({
  title,
  titleIcons,
  headerButtons,
  onTitleClick,
  orgLabel,
  notifications,
  syncStatus,
  profile,
  support,
}: AdminChromePageTitleBarProps) => (
  <div style={HEADER_STYLE}>
    <div style={LEFT_SECTION_STYLE}>
      <div
        style={{
          ...TITLE_GROUP_STYLE,
          cursor: onTitleClick ? 'pointer' : 'default',
        }}
        onClick={onTitleClick}
      >
        <span style={TITLE_TEXT_STYLE}>{title}</span>
        {titleIcons}
      </div>
      {headerButtons
        ? (
          <span
            aria-hidden='true'
            data-kai-shiki-admin-chrome-title-divider='true'
            style={TITLE_DIVIDER_STYLE}
          />
        )
        : null}
      {headerButtons}
    </div>
    <div style={ACTIONS_STYLE}>
      {orgLabel ? <div style={ORG_WIDGET_STYLE}>{orgLabel}</div> : null}
      {support}
      {notifications}
      {syncStatus ? <div style={STATUS_STYLE}>{syncStatus}</div> : null}
      {profile}
    </div>
  </div>
);
